import React, { useEffect, useState, useCallback } from 'react';
import {
  Card, CardBody, CardHeader, Modal, Row, Col, Input, Button, Spinner, Alert, Badge,
} from 'reactstrap';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import FeatherIcon from 'feather-icons-react';
import usePermission from '../Hooks/usePermission';
import PermissionGuard from '../components/Common/PermissionGuard';
import AssetModal from '../components/AssetModal';
import api from '../helpers/api';
import { formatCreatedDate } from '../constants/utils';
import {
  METER_CREATE, METER_UPDATE, METER_DELETE,
} from '../constants/permissions';
import {
  fetchMeters,
  deleteMeter,
  clearMeterMessages,
} from '../store/meter/actions';
import MeterForm from './MeterForm';
import MeterDetailModal from './MeterDetail';

const STATUS_COLORS = {
  PENDING: 'secondary',
  STABLE: 'success',
  WARNING: 'warning',
  ALARM: 'danger',
  OVERDUE: 'info',
};


const Meters = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { hasPermission } = usePermission();

  const {
    meters, metersLoading, metersError, metersPagination,
    saving, saveError, saveSuccess,
  } = useSelector((state) => state.Meter);

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(0);
  const [showForm, setShowForm] = useState(false);
  const [editingMeter, setEditingMeter] = useState(null);
  const [deleteModal, setDeleteModal] = useState(null);
  const [filterModal, setFilterModal] = useState(false);
  const [detailMeterId, setDetailMeterId] = useState(null);
  const [assetModalData, setAssetModalData] = useState(null);
  const [assetModalLoading, setAssetModalLoading] = useState(false);

  const pageSize = 15;

  useEffect(() => {
    document.title = `${t('meters.title', 'Meters')} | Arcmedix`;
  }, [t]);

  useEffect(() => {
    dispatch(fetchMeters(page, pageSize, null, statusFilter || null, search || null));
  }, [dispatch, page, statusFilter, search]);

  useEffect(() => {
    if (saveSuccess || saveError) {
      const timer = setTimeout(() => dispatch(clearMeterMessages()), 5000);
      return () => clearTimeout(timer);
    }
  }, [saveSuccess, saveError, dispatch]);

  const handleCreate = useCallback(() => {
    setEditingMeter(null);
    setShowForm(true);
  }, []);

  const handleEdit = useCallback((e, meter) => {
    e.stopPropagation();
    setEditingMeter(meter);
    setShowForm(true);
  }, []);

  const handleFormClose = useCallback(() => {
    setShowForm(false);
    setEditingMeter(null);
    dispatch(fetchMeters(page, pageSize, null, statusFilter || null, search || null));
  }, [dispatch, page, statusFilter, search]);

  const handleDelete = useCallback(() => {
    if (deleteModal) {
      dispatch(deleteMeter(deleteModal.id, () => {
        setDeleteModal(null);
        dispatch(fetchMeters(page, pageSize, null, statusFilter || null, search || null));
      }));
    }
  }, [deleteModal, dispatch, page, statusFilter, search]);

  const handleRowClick = useCallback((meter) => {
    setDetailMeterId(meter.id);
  }, []);

  const handleSearchChange = useCallback((e) => {
    setSearch(e.target.value);
    setPage(0);
  }, []);

  const handleAssetClick = useCallback(async (e, meter) => {
    e.stopPropagation();
    if (!meter.assetId) return;
    try {
      setAssetModalLoading(true);
      const { data } = await api.get(`asset/get-by-id?id=${meter.assetId}`);
      if (data?.response) {
        setAssetModalData(data.response);
      }
    } catch (error) {
      console.error('Error fetching asset:', error);
    } finally {
      setAssetModalLoading(false);
    }
  }, []);

  const totalPages = metersPagination?.totalPages || 1;

  return (
    <div className="page-content mt-4">
      {saveSuccess && <Alert color="success" className="mb-3">{saveSuccess}</Alert>}
      {saveError && <Alert color="danger" className="mb-3">{saveError}</Alert>}

      {/* Top Bar */}
      <div className="mb-4 d-flex align-items-center gap-3">
        <motion.div
          className="mb-4 d-flex align-items-center gap-3"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <PermissionGuard permission={METER_CREATE}>
            <button
              className="btn btn-success"
              onClick={handleCreate}
              title={t('meters.add_new_meter', 'Add new meter')}
            >
              <FeatherIcon icon="plus" size={18} className="me-1" />
              {t('meters.create', 'Create Meter')}
            </button>
          </PermissionGuard>
          <div
            className="ms-auto"
            style={{ minWidth: '400px', flexGrow: 1, maxWidth: '500px' }}
          >
            <Input
              type="text"
              placeholder={t('meters.search_placeholder', 'Search meters...')}
              value={search}
              onChange={handleSearchChange}
            />
          </div>
        </motion.div>
      </div>

      <Card>
        <CardHeader>
          <div className="d-flex justify-content-between align-items-center">
            <h3 className="card-title text-success mb-0">{t('meters.title', 'Meters')}</h3>
            <div className="d-flex align-items-center gap-2">
              <button
                className="btn btn-soft-primary p-2"
                onClick={() => setFilterModal(true)}
                title={t('meters.filter_meters', 'Filter meters')}
              >
                <FeatherIcon icon="filter" size={18} />
              </button>
            </div>
          </div>
        </CardHeader>
        <CardBody>
          <div
            className="table-responsive"
            style={{
              overflowX: 'auto',
              whiteSpace: 'nowrap',
              maxHeight: 'calc(100vh - 300px)',
              overflowY: 'auto',
            }}
          >
            <table
              className="table table-hover mb-0"
              style={{
                borderSpacing: '0.1rem',
                borderCollapse: 'separate',
              }}
            >
              <thead
                style={{
                  position: 'sticky',
                  top: 0,
                  background: 'white',
                  zIndex: 1,
                }}
              >
                <tr>
                  <th>{t('meters.name', 'Name')}</th>
                  <th>{t('meters.asset', 'Asset')}</th>
                  <th>{t('shared.unit', 'Unit')}</th>
                  <th>{t('meters.last_reading', 'Last Reading')}</th>
                  <th>{t('shared.status', 'Status')}</th>
                  <th>{t('meters.last_read_date', 'Last Read Date')}</th>
                  <th>{t('shared.actions', 'Actions')}</th>
                </tr>
              </thead>
              <tbody>
                {metersLoading ? (
                  <tr>
                    <td colSpan="7" className="text-center py-5">
                      <Spinner color="primary" />
                    </td>
                  </tr>
                ) : metersError ? (
                  <tr>
                    <td colSpan="7">
                      <Alert color="danger" className="mb-0">{metersError}</Alert>
                    </td>
                  </tr>
                ) : meters.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="text-center py-5 text-muted">
                      {search
                        ? t('meters.no_matches', 'No meters match your search')
                        : t('meters.no_meters', 'No meters found.')}
                    </td>
                  </tr>
                ) : (
                  <AnimatePresence>
                    {meters.map((meter, ind) => (
                      <motion.tr
                        key={meter.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ duration: 0.3 }}
                        onClick={() => handleRowClick(meter)}
                        style={{ cursor: 'pointer' }}
                      >
                        <td>
                          <span
                            className="text-primary cursor-pointer hover-underline fw-semibold"
                            style={{ cursor: 'pointer' }}
                          >
                            {meter.name}
                          </span>
                        </td>
                        <td>
                          {meter.assetId ? (
                            <span
                              className="text-primary cursor-pointer hover-underline"
                              style={{ cursor: 'pointer' }}
                              onClick={(e) => handleAssetClick(e, meter)}
                            >
                              {meter.assetName || '-'}
                            </span>
                          ) : (
                            meter.assetName || '-'
                          )}
                        </td>
                        <td>{meter.measurementUnit ? t(`meters.unit_${meter.measurementUnit.toLowerCase().replace(/[\s/²]+/g, '_')}`, meter.measurementUnit) : '-'}</td>
                        <td>{meter.lastReadingValue != null ? meter.lastReadingValue : '-'}</td>
                        <td>
                          <Badge color={STATUS_COLORS[meter.status] || 'secondary'} pill className="py-1 px-2">
                            {meter.status ? t(`meters.status_${meter.status.toLowerCase()}`, meter.status) : '-'}
                          </Badge>
                        </td>
                        <td>
                          {meter.lastReadingAt
                            ? new Date(meter.lastReadingAt).toLocaleDateString('en-GB')
                            : '-'}
                        </td>
                        <td>
                          <div className="d-flex gap-1" onClick={(e) => e.stopPropagation()}>
                            {hasPermission(METER_UPDATE) && (
                              <button
                                className="btn btn-soft-primary p-1"
                                onClick={(e) => handleEdit(e, meter)}
                                title={t('shared.edit', 'Edit')}
                              >
                                <FeatherIcon icon="edit-2" size={14} />
                              </button>
                            )}
                            {hasPermission(METER_DELETE) && (
                              <span
                                className="text-danger"
                                role="button"
                                onClick={() => setDeleteModal(meter)}
                              >
                                <FeatherIcon icon="trash-2" />
                              </span>
                            )}
                          </div>
                        </td>
                      </motion.tr>
                    ))}
                  </AnimatePresence>
                )}
              </tbody>
            </table>

            {!metersLoading && meters.length > 0 && (
              <div className="d-flex justify-content-between align-items-center mt-3 px-2">
                <small className="text-muted">
                  {t('meters.showing', 'Showing')} {meters.length} {t('shared.of', 'of')}{' '}
                  {metersPagination?.totalElements || meters.length}
                </small>
                {totalPages > 1 && (
                  <div className="d-flex gap-1">
                    <Button
                      color="outline-secondary"
                      size="sm"
                      disabled={page === 0}
                      onClick={() => setPage((p) => p - 1)}
                    >
                      <FeatherIcon icon="chevron-left" size={16} />
                    </Button>
                    <span className="d-flex align-items-center px-2 small">
                      {page + 1} / {totalPages}
                    </span>
                    <Button
                      color="outline-secondary"
                      size="sm"
                      disabled={page + 1 >= totalPages}
                      onClick={() => setPage((p) => p + 1)}
                    >
                      <FeatherIcon icon="chevron-right" size={16} />
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        </CardBody>
      </Card>

      {/* Filter Modal */}
      <Modal
        isOpen={filterModal}
        toggle={() => setFilterModal(false)}
        centered
        className="fade-scale-modal"
        backdropClassName="modal-backdrop-blur"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          transition={{ duration: 0.2 }}
        >
          <div className="modal-header text-white">
            <h5 className="modal-title fs-4">{t('meters.filter_meters', 'Filter Meters')}</h5>
            <button
              type="button"
              className="btn-close btn-close-white"
              onClick={() => setFilterModal(false)}
              aria-label={t('shared.close', 'Close')}
            ></button>
          </div>

          <div className="modal-body p-4">
            <Row className="mb-4 g-3">
              <Col md={12}>
                <div className="form-group">
                  <label htmlFor="filterStatus" className="form-label fw-medium">
                    {t('shared.status', 'Status')}
                  </label>
                  <select
                    id="filterStatus"
                    className="form-select"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <option value="">{t('meters.all_statuses', 'All Statuses')}</option>
                    <option value="PENDING">{t('meters.status_pending', 'Pending')}</option>
                    <option value="STABLE">{t('meters.status_stable', 'Stable')}</option>
                    <option value="WARNING">{t('meters.status_warning', 'Warning')}</option>
                    <option value="ALARM">{t('meters.status_alarm', 'Alarm')}</option>
                    <option value="OVERDUE">{t('meters.status_overdue', 'Overdue')}</option>
                  </select>
                </div>
              </Col>
            </Row>
          </div>

          <div className="modal-footer bg-light border-top-0">
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={() => {
                setStatusFilter('');
                setFilterModal(false);
              }}
            >
              {t('shared.clear_all', 'Clear All')}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => setFilterModal(false)}
            >
              {t('shared.apply_filters', 'Apply Filters')}
            </button>
          </div>
        </motion.div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={!!deleteModal} toggle={() => setDeleteModal(null)} centered>
        <div className="modal-header">
          <h5 className="modal-title">{t('meters.confirm_delete_title', 'Delete Meter')}</h5>
          <button
            type="button"
            className="btn-close"
            onClick={() => setDeleteModal(null)}
            aria-label={t('shared.close', 'Close')}
          ></button>
        </div>
        <div className="modal-body">
          <p>
            {t('meters.confirm_delete', 'Are you sure you want to delete this meter? This action cannot be undone.')}
          </p>
          <p className="fw-bold">{deleteModal?.name}</p>
        </div>
        <div className="modal-footer">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setDeleteModal(null)}
          >
            {t('shared.cancel', 'Cancel')}
          </button>
          <button
            type="button"
            className="btn btn-danger"
            onClick={handleDelete}
            disabled={saving}
          >
            {saving ? <Spinner size="sm" /> : t('shared.delete', 'Delete')}
          </button>
        </div>
      </Modal>

      {/* Create/Edit Form Modal */}
      <MeterForm
        isOpen={showForm}
        toggle={handleFormClose}
        editData={editingMeter}
        onSave={handleFormClose}
      />

      {/* Detail Modal */}
      <MeterDetailModal
        isOpen={!!detailMeterId}
        toggle={() => setDetailMeterId(null)}
        meterId={detailMeterId}
        onUpdate={() => dispatch(fetchMeters(page, pageSize, null, statusFilter || null, search || null))}
      />

      {/* Asset Modal */}
      <AssetModal
        isOpen={!!assetModalData}
        toggle={() => setAssetModalData(null)}
        userData={assetModalData}
        loading={assetModalLoading}
        formatCreatedDate={formatCreatedDate}
      />
    </div>
  );
};

export default Meters;
